/* eslint-disable max-lines */
/**
 * Core LingDatabaseSDK client implementation
 *
 * This module provides the main SDK client class for interacting with Ling's backend services.
 * It implements a singleton pattern and provides access to various service modules through
 * a secure internal access control mechanism.
 */

import {
  handleHttpError,
  createErrorFromResponse,
  NetworkError,
} from '../errors';
import { OfflineOperations } from './offline-operations';
import { OfflineQueue } from '../offline/queue';
import { CurriculumService } from '../services/curriculum-service';
import { FavoritesService } from '../services/favorites-service';
import { ProfileService } from '../services/profile-service';
import { ProgressService } from '../services/progress-service';
import { SettingsService } from '../services/settings-service';
import { StreakService } from '../services/streak-service';
import { WebsocketService } from '../services/websocket-service';
import {
  LingDatabaseSDKConfig,
  ServiceName,
  ServiceEndpointConfig,
  ApiSuccessResponse,
  InternalSDKOperations,
  RequestOptions,
  HttpMethod,
  ApiResponse,
} from '../types';
import { getBaseUrl } from '../utils/http';
import { NetworkDetectionManager } from '../utils/network-detection-manager';
import { retryWithExponentialBackoff } from '../utils/retry';
import { generateTraceId } from '../utils/tracing';
import { validateConfig } from '../utils/validation';

/**
 * Main SDK client class for Ling Database interactions.
 *
 * This class implements the singleton pattern to ensure only one instance exists per application.
 * It provides access to various backend services through dedicated service classes while
 * maintaining security through internal access control.
 */
export class LingDatabaseSDK {
  /** Singleton instance storage */
  private static instance: LingDatabaseSDK | null = null;

  /** Function to retrieve authentication token */
  private getAuthToken: () => Promise<string>;

  /** Base URL for API requests with service name placeholder */
  private baseUrl: string;

  /** Service-specific endpoint overrides */
  private serviceEndpoints?: Partial<
    Record<ServiceName, ServiceEndpointConfig>
  >;

  /** App version sent as the `x-app-version` header */
  private appVersion?: string;

  /** Platform identifier sent as the `x-platform` header */
  private platform?: string;

  /** Offline queue for handling queued operations */
  private offlineQueue?: OfflineQueue;

  /** Centralized network detection manager */
  private networkManager?: NetworkDetectionManager;

  /** Service instances for different backend modules */
  public curriculum: CurriculumService;
  public favorites: FavoritesService;
  public profile: ProfileService;
  public progress: ProgressService;
  public settings: SettingsService;
  public streak: StreakService;
  public websocket: WebsocketService;

  /** Offline operations namespace */
  public offline: OfflineOperations;

  /**
   * Private constructor to enforce singleton pattern.
   * Initializes the SDK with configuration and creates service instances.
   *
   * @param config - SDK configuration including environment and auth token provider
   */
  private constructor(config: LingDatabaseSDKConfig) {
    this.getAuthToken = config.getAuthToken;
    this.baseUrl = config.customEndpoint || getBaseUrl(config.environment);
    this.serviceEndpoints = config.serviceEndpoints;
    this.appVersion = config.appVersion;
    this.platform = config.platform;

    // Initialize centralized network detection if offline mode is enabled
    if (config.offline?.enabled) {
      this.networkManager = new NetworkDetectionManager(
        config.offline.networkDetector,
      );
      this.initializeOfflineMode(config);
    }

    // Initialize all service instances with controlled access to internal operations
    const internalOps = this.getInternalOperations();

    this.curriculum = new CurriculumService(internalOps, this.networkManager);
    this.favorites = new FavoritesService(internalOps, this.networkManager);
    this.profile = new ProfileService(internalOps, this.networkManager);
    this.progress = new ProgressService(internalOps, this.networkManager);
    this.settings = new SettingsService(internalOps, this.networkManager);
    this.streak = new StreakService(internalOps, this.networkManager);
    this.websocket = new WebsocketService(internalOps);

    // Initialize offline operations namespace
    this.offline = new OfflineOperations(this.offlineQueue);
  }

  /**
   * Initializes the SDK singleton instance.
   * If an instance already exists, returns the existing instance with a warning.
   *
   * @param config - SDK configuration object
   * @returns The SDK instance
   * @throws {Error} If the configuration is invalid
   */
  public static initialize(config: LingDatabaseSDKConfig): LingDatabaseSDK {
    if (LingDatabaseSDK.instance) {
      console.warn(
        'LingDatabaseSDK is already initialized. Returning existing instance.',
      );
      return LingDatabaseSDK.instance;
    }

    validateConfig(config);

    LingDatabaseSDK.instance = new LingDatabaseSDK(config);
    return LingDatabaseSDK.instance;
  }

  /**
   * Destroys the current SDK instance.
   * Properly cleans up all resources including network listeners and queued operations.
   * Useful for testing or when you need to reinitialize with different configuration.
   */
  public static destroyInstance(): void {
    if (LingDatabaseSDK.instance) {
      // Clean up offline queue first (preserves operations in storage)
      if (LingDatabaseSDK.instance.offlineQueue) {
        LingDatabaseSDK.instance.offlineQueue.destroy();
      }

      // Clean up the centralized network manager last
      if (LingDatabaseSDK.instance.networkManager) {
        LingDatabaseSDK.instance.networkManager.destroy();
      }
    }
    LingDatabaseSDK.instance = null;
  }

  /**
   * Initializes offline mode functionality
   *
   * @param config - SDK configuration with offline settings
   */
  private initializeOfflineMode(config: LingDatabaseSDKConfig): void {
    if (!config.offline?.enabled) {
      return;
    }

    this.offlineQueue = new OfflineQueue({
      maxQueueSize: config.offline.maxQueueSize ?? 100,
      maxRetries: config.offline.maxRetries ?? 3,
      networkManager: this.networkManager,
      storageAdapter: config.offline.storageAdapter,
      storageKey: 'ling-db-sdk-offline-queue',
      onPermanentFailure: config.offline.onPermanentFailure,
      onAutoSyncFailure: config.offline.onAutoSyncFailure,
      onOperationQueued: config.offline.onOperationQueued,
    });

    // Set up operation executor
    this.offlineQueue.setExecutor(
      async (
        service: string,
        method: string,
        params: Record<string, unknown>,
      ) => {
        const result = await this.makeInternalRequest(service, method, params);
        return result.data;
      },
    );
  }

  /**
   * Retrieves authentication token with retry logic.
   * Validates that the token is a non-empty string before returning.
   *
   * @returns Promise that resolves to a valid authentication token
   * @throws {Error} If unable to retrieve a valid token after retries
   */
  private async getAuthTokenWithRetry(): Promise<string> {
    return retryWithExponentialBackoff(async () => {
      const token = await this.getAuthToken();
      if (token && typeof token === 'string' && token.trim().length > 0) {
        return token;
      }
      throw new Error('Invalid token received');
    }, 'getting auth token');
  }

  /**
   * Makes HTTP requests with retry logic using exponential backoff.
   *
   * @param url - The URL to fetch
   * @param options - Fetch options (headers, method, body, etc.)
   * @returns Promise that resolves to the HTTP response
   * @throws {NetworkError} If the request fails after all retries
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
  ): Promise<Response> {
    return retryWithExponentialBackoff(
      () => fetch(url, options),
      'making fetch request',
    );
  }

  /**
   * Provides controlled access to internal SDK operations for service classes
   * This method is not exported and can only be accessed by service classes
   * during their initialization within the SDK constructor
   */
  private getInternalOperations(): InternalSDKOperations {
    return {
      getAuthToken: this.getAuthTokenWithRetry.bind(this),
      getServiceUrl: this.getServiceUrl.bind(this),
      isOfflineModeEnabled: () => !!this.offlineQueue,
      makeRequest: this.makeInternalRequest.bind(this),
      queueOperation: this.queueOperation.bind(this),
    };
  }

  /**
   * Gets the base URL for a specific service
   * Centralizes URL construction logic for all services with support for service-specific overrides
   */
  private getServiceUrl(service: string): string {
    // Check for service-specific endpoint override
    const serviceOverride =
      this.serviceEndpoints?.[service as ServiceName]?.endpoint;

    if (serviceOverride) {
      console.log(
        `Service endpoint override found for ${service}: ${serviceOverride}`,
      );
      return serviceOverride;
    }

    // Use the default base URL with service name substitution
    return this.baseUrl.replace('{{service-name}}', service);
  }

  /**
   * Builds the complete request URL with optional query parameters
   */
  private buildRequestUrl(
    service: string,
    version: string,
    endpoint: string,
    queryParams?: Record<string, string | number | boolean>,
  ): string {
    const serviceUrl = this.getServiceUrl(service);
    let url = `${serviceUrl}/${version}/${endpoint}`;

    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      }
      url = `${url}?${params.toString()}`;
    }

    return url;
  }

  /**
   * Builds fetch options based on HTTP method
   */
  private buildFetchOptions(
    method: HttpMethod,
    token: string,
    traceId: string,
    data?: Record<string, unknown>,
  ): RequestInit {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'X-Trace-ID': traceId,
    };

    if (this.appVersion) {
      headers['x-app-version'] = this.appVersion;
    }

    if (this.platform) {
      headers['x-platform'] = this.platform;
    }

    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    return {
      headers,
      method,
      ...(method !== 'GET' && data && { body: JSON.stringify(data) }),
    };
  }

  /**
   * Private method for making authenticated requests to backend services.
   * This method can only be accessed through the InternalSDKOperations interface.
   *
   * @template T - The expected response data type
   * @param service - The backend service name (e.g., 'profile', 'favorites')
   * @param endpoint - The API endpoint within the service
   * @param data - Optional request payload data (used for POST body)
   * @param version - API version to use (defaults to 'v1')
   * @param options - Optional request configuration (method, queryParams)
   * @returns Promise resolving to the API response with typed data
   *
   * @throws {NetworkError} If network request fails
   * @throws {ApiError} If the API returns an error response
   */
  private async makeInternalRequest<T>(
    service: string,
    endpoint: string,
    data: Record<string, unknown> = {},
    version = 'v1',
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    try {
      const token = await this.getAuthTokenWithRetry();
      const traceId = generateTraceId();
      const { method = 'POST', queryParams } = options;

      const url = this.buildRequestUrl(service, version, endpoint, queryParams);
      const fetchOptions = this.buildFetchOptions(method, token, traceId, data);
      const response = await this.fetchWithRetry(url, fetchOptions);

      // Handle HTTP error responses
      if (!response.ok) {
        let responseBody: unknown;
        try {
          responseBody = await response.json();
        } catch {
          // If we can't parse the response, proceed with HTTP error handling without body
        }

        throw handleHttpError(
          response.status,
          response.statusText,
          responseBody,
        );
      }

      // Parse and validate the response
      const result = (await response.json()) as ApiResponse;

      // Check if the API response indicates success
      if (!result.success) {
        throw createErrorFromResponse(
          result as {
            success: false;
            error: { code: string; message: string };
          },
        );
      }

      return result as ApiSuccessResponse<T>;
    } catch (error) {
      // Log the error for debugging purposes
      console.error(`Error making request to ${service}/${endpoint}:`, error);

      // Convert network-related errors to NetworkError for better error handling
      if (
        error instanceof TypeError ||
        (error as Error & { cause?: { code: string } })?.cause?.code ===
          'ENOTFOUND'
      ) {
        const message =
          error instanceof Error ? error.message : 'Unknown network error';
        throw new NetworkError(`Network error: ${message}`, error);
      }

      // Re-throw other errors as-is (API errors, access control errors, etc.)
      throw error;
    }
  }

  /**
   * Queue an operation for offline execution (simplified)
   *
   * @param service - The service name
   * @param method - The method name
   * @param parameters - Operation parameters
   * @returns Promise resolving to the operation ID
   */
  private async queueOperation(
    service: string,
    method: string,
    parameters: Record<string, unknown>,
  ): Promise<string> {
    if (!this.offlineQueue) {
      throw new Error('Offline mode is not enabled');
    }

    return this.offlineQueue.add(service, method, parameters);
  }
}
